/**
 * CRUD操作相关的自定义Hooks
 *
 * 提供统一的数据处理能力，包括：
 * - 数据加载 (useLoadList)
 * - 创建数据 (useCreate)
 * - 更新数据 (useUpdate)
 * - 删除数据 (useDelete)
 */

import { useState, useCallback, useEffect, useRef } from "react";
import { message } from "antd";
import { isEqual } from "lodash";

const describeLoadError = (err, resourceName) => {
    const data = err?.response?.data;
    if (typeof data?.detail === "string" && data.detail) return data.detail;
    if (typeof data?.message === "string" && data.message) return data.message;
    if (!err?.response) return "网络异常，请检查网络连接";
    if (err.response.status === 403) return `没有查看${resourceName}的权限`;
    if (err.response.status === 404) return "请求的资源不存在";
    if (err.response.status >= 500) return "服务器错误，请稍后再试";
    return `加载${resourceName}列表失败`;
};

/**
 * 用于加载列表数据的Hook
 * @param {string} resourceName - 资源名称，用于日志和错误提示
 * @param {Function} apiFunction - API函数，接收params参数并返回Promise
 * @param {Object} params - 查询参数
 * @param {Object} options - 配置选项
 * @param {boolean} options.immediate - 是否立即加载数据，默认为true
 * @param {Function} options.onSuccess - 成功回调
 * @param {Function} options.onError - 错误回调
 * @returns {Object} 包含数据、加载状态、错误信息和重新加载方法的对象
 */
export function useLoadList(resourceName, apiFunction, params = {}, options = {}) {
    const { immediate = true, onSuccess, onError } = options;
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const dataRef = useRef(null);

    // 使用useRef存储资源名称、API函数、参数和回调，防止useEffect频繁触发
    const resourceNameRef = useRef(resourceName);
    const apiFunctionRef = useRef(apiFunction);
    const paramsRef = useRef(params);
    const onSuccessRef = useRef(onSuccess);
    const onErrorRef = useRef(onError);

    // 当参数变化时更新ref
    useEffect(() => {
        resourceNameRef.current = resourceName;
        apiFunctionRef.current = apiFunction;

        // 只有当params实际内容变化时才更新paramsRef
        if (!isEqual(paramsRef.current, params)) {
            paramsRef.current = params;
        }

        onSuccessRef.current = onSuccess;
        onErrorRef.current = onError;
    }, [resourceName, apiFunction, params, onSuccess, onError]);

    const fetchData = useCallback(
        async (customParams = {}) => {
            try {
                setIsLoading(true);
                setError(null);

                // 使用ref中保存的值
                const currentResourceName = resourceNameRef.current;
                const currentApiFunction = apiFunctionRef.current;
                const currentParams = paramsRef.current;
                const currentOnSuccess = onSuccessRef.current;

                // 合并默认参数和自定义参数
                const mergedParams = { ...currentParams, ...customParams };

                // 增强后的API函数返回完整的response对象
                const response = await currentApiFunction(mergedParams);

                // 根据API_GUIDELINES.md规范，后端必须返回标准的PaginatedResponse格式
                // 前端严格禁止添加任何兼容逻辑，直接使用后端返回的标准格式
                const responseData = response?.data;

                // 验证后端是否遵循API规范
                if (!responseData || !responseData.items || typeof responseData.total !== "number") {
                    throw new Error("后端API响应不符合PaginatedResponse规范，请检查API实现");
                }

                dataRef.current = responseData;
                setData(responseData);
                if (currentOnSuccess) currentOnSuccess(responseData);
                return responseData;
            } catch (err) {
                console.error(`加载${resourceNameRef.current}列表失败:`, err);
                setError(describeLoadError(err, resourceNameRef.current));
                if (onErrorRef.current) onErrorRef.current(err);
                // 刷新失败时留下已经看到的数据。第一次就失败才给空表，避免表格读到空值。
                if (dataRef.current) return dataRef.current;
                const emptyData = { items: [], total: 0 };
                dataRef.current = emptyData;
                setData(emptyData);
                return emptyData;
            } finally {
                setIsLoading(false);
            }
        },
        [], // fetchData不依赖任何变化的值，避免重复渲染和请求
    );

    // 监听params变化重新加载数据
    const prevParamsRef = useRef(params);

    useEffect(() => {
        // 只在params真正变化时触发重新加载
        if (immediate && !isEqual(prevParamsRef.current, params)) {
            prevParamsRef.current = params;
            fetchData();
        }
    }, [params, immediate, fetchData]);

    // 首次加载
    const initialLoadRef = useRef(false);

    useEffect(() => {
        if (immediate && !initialLoadRef.current) {
            initialLoadRef.current = true;
            fetchData();
        }
    }, [immediate, fetchData]);

    return {
        data,
        isLoading,
        error,
        refetch: fetchData,
    };
}

/**
 * 用于创建资源的Hook
 * @param {string} resourceName - 资源名称，用于日志和错误提示
 * @param {Function} apiFunction - API函数，接收data参数并返回Promise
 * @param {Object} options - 配置选项
 * @param {Function} options.onSuccess - 成功回调
 * @param {Function} options.onError - 错误回调
 * @returns {Object} 包含提交方法、加载状态和错误信息的对象
 */
export function useCreate(resourceName, apiFunction, options = {}) {
    const { onSuccess, onError } = options;
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const mutate = useCallback(
        async (data) => {
            try {
                setIsLoading(true);
                setError(null);

                const response = await apiFunction(data);

                // 按照API规范处理响应
                if (response.data && response.data.success) {
                    // 使用后端返回的具体消息
                    const successMessage = response.data.message || `创建${resourceName}成功`;
                    message.success(successMessage);
                    if (onSuccess) onSuccess(response);
                    return response;
                } else {
                    // 如果没有success字段，兼容旧格式（直接返回资源对象）
                    message.success(`创建${resourceName}成功`);
                    if (onSuccess) onSuccess(response);
                    return response;
                }
            } catch (err) {
                console.error(`创建${resourceName}失败:`, err);

                // 优先使用后端返回的错误消息
                let errorMsg = `创建${resourceName}失败`;
                if (err.response?.data?.detail) {
                    errorMsg = err.response.data.detail;
                } else if (err.response?.data?.message) {
                    errorMsg = err.response.data.message;
                } else if (err.message) {
                    errorMsg = err.message;
                }

                setError(errorMsg);
                message.error(errorMsg);
                if (onError) onError(err);
                // 不再重新抛出错误，避免未捕获的异常覆盖页面
                // throw err;
                return null; // 返回null表示操作失败
            } finally {
                setIsLoading(false);
            }
        },
        [apiFunction, resourceName, onSuccess, onError],
    );

    return {
        mutate,
        isLoading,
        error,
    };
}

/**
 * 用于更新资源的Hook
 * @param {string} resourceName - 资源名称，用于日志和错误提示
 * @param {Function} apiFunction - API函数，接收{ id, data }参数并返回Promise
 * @param {Object} options - 配置选项
 * @param {Function} options.onSuccess - 成功回调
 * @param {Function} options.onError - 错误回调
 * @returns {Object} 包含提交方法、加载状态和错误信息的对象
 */
export function useUpdate(resourceName, apiFunction, options = {}) {
    const { onSuccess, onError } = options;
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const mutate = useCallback(
        async ({ id, data }) => {
            try {
                setIsLoading(true);
                setError(null);

                const response = await apiFunction({ id, data });

                // 按照API规范处理响应
                if (response.data && response.data.success) {
                    // 使用后端返回的具体消息
                    const successMessage = response.data.message || `更新${resourceName}成功`;
                    message.success(successMessage);
                    if (onSuccess) onSuccess(response);
                    return response;
                } else {
                    // 如果没有success字段，兼容旧格式（直接返回资源对象）
                    message.success(`更新${resourceName}成功`);
                    if (onSuccess) onSuccess(response);
                    return response;
                }
            } catch (err) {
                console.error(`更新${resourceName}失败:`, err);

                // 优先使用后端返回的错误消息
                let errorMsg = `更新${resourceName}失败`;
                if (err.response?.data?.detail) {
                    errorMsg = err.response.data.detail;
                } else if (err.response?.data?.message) {
                    errorMsg = err.response.data.message;
                } else if (err.message) {
                    errorMsg = err.message;
                }

                setError(errorMsg);
                message.error(errorMsg);
                if (onError) onError(err);
                // 不再重新抛出错误，避免未捕获的异常覆盖页面
                // throw err;
                return null; // 返回null表示操作失败
            } finally {
                setIsLoading(false);
            }
        },
        [apiFunction, resourceName, onSuccess, onError],
    );

    return {
        mutate,
        isLoading,
        error,
    };
}

/**
 * 用于删除资源的Hook
 * @param {string} resourceName - 资源名称，用于日志和错误提示
 * @param {Function} apiFunction - API函数，接收id参数并返回Promise
 * @param {Object} options - 配置选项
 * @param {Function} options.onSuccess - 成功回调
 * @param {Function} options.onError - 错误回调
 * @returns {Object} 包含提交方法、加载状态和错误信息的对象
 */
export function useDelete(resourceName, apiFunction, options = {}) {
    const { onSuccess, onError } = options;
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const mutate = useCallback(
        async (id) => {
            try {
                setIsLoading(true);
                setError(null);

                const response = await apiFunction(id);

                // 按照API规范处理响应
                if (response.data && response.data.success) {
                    // 使用后端返回的具体消息
                    const successMessage = response.data.message || `删除${resourceName}成功`;
                    message.success(successMessage);
                    if (onSuccess) onSuccess(response);
                    return response;
                } else {
                    // 如果没有success字段，兼容旧格式
                    message.success(`删除${resourceName}成功`);
                    if (onSuccess) onSuccess(response);
                    return response;
                }
            } catch (err) {
                console.error(`删除${resourceName}失败:`, err);

                // 优先使用后端返回的错误消息
                let errorMsg = `删除${resourceName}失败`;
                if (err.response?.data?.detail) {
                    errorMsg = err.response.data.detail;
                } else if (err.response?.data?.message) {
                    errorMsg = err.response.data.message;
                } else if (err.message) {
                    errorMsg = err.message;
                }

                setError(errorMsg);
                message.error(errorMsg);
                if (onError) onError(err);
                // 不再重新抛出错误，避免未捕获的异常覆盖页面
                // throw err;
                return null; // 返回null表示操作失败
            } finally {
                setIsLoading(false);
            }
        },
        [apiFunction, resourceName, onSuccess, onError],
    );

    return {
        mutate,
        isLoading,
        error,
    };
}

// 默认导出
export default {
    useLoadList,
    useCreate,
    useUpdate,
    useDelete,
};
